import { getSelectedOrPaidOffers } from "@/pages/Job/utils";
import RecurringJobsDiscoverModal from "@/pages/recurring-jobs/RecurringJobsDiscoverModal";
import {
  DEFERRED_PREPAYMENT,
  SHOW_RECURRING_JOBS_MODAL_URL_PARAM,
} from "@/utils";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { Block, Container } from "../../components";
import JobSkeleton from "../../pages/Job/skeletons/JobSkeleton";
import JobRecurringSuccessModal from "../../pages/jobSuccess/JobRecurringSuccessModal";
import JobSuccessModal from "../../pages/jobSuccess/JobSuccessModal";
import polyglot from "../../utils/polyglot";
import { JobContext } from "../contexts/index";
import { useBreakpoints } from "../hooks";
import { useGetJob, useGetPendingOffers } from "../routes/job-routes";
import { useModals } from "./ModalProvider";

const JobProvider = ({
  children,
  public_pages,
  show_success,
  jobbers,
  job_type,
}) => {
  const { data, isSuccess } = useGetJob();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const { current_user } = useSelector((state) => state.rails);
  const pendingOffers = useGetPendingOffers();
  const breakpoints = useBreakpoints();
  const [isAssistant, setIsAssistant] = useState(false);
  const [isAssistantInitial, setIsAssistantInitial] = useState(false);
  const [isPoster, setIsPoster] = useState(false);
  const history = useHistory();
  const isRecurring = job_type === "recurring";
  const modals = useModals();

  useEffect(() => {
    const recurringSuccessOfferId = new URLSearchParams(
      window.location.search
    ).get(SHOW_RECURRING_JOBS_MODAL_URL_PARAM);
    if (recurringSuccessOfferId && isSuccess && data) {
      const offer = data.accepted_offers.find(
        (offer) => Number(offer.id) === Number(recurringSuccessOfferId)
      );
      if (offer?.jobber) {
        modals.open(RecurringJobsDiscoverModal, {
          jobber: offer.jobber,
        });
      }
      const newSearch = (() => {
        const params = new URLSearchParams(history.location.search);
        params.delete(SHOW_RECURRING_JOBS_MODAL_URL_PARAM);
        return params.toString() ? `?${params.toString()}` : "";
      })();

      window.history.replaceState(
        null,
        "",
        history.location.pathname + newSearch
      );
    }
  }, [isSuccess]);

  useEffect(() => {
    if (show_success && !isRecurring) {
      setShowSuccessModal(true);
      return;
    }
    if (isRecurring && show_success && isSuccess) {
      // data depends on the jobber data, so we need to wait for the jobber to be loaded
      setShowSuccessModal(true);
    }
  }, [show_success, isSuccess, isRecurring]);

  useEffect(() => {
    if (isSuccess && current_user && current_user.id === data.poster?.id) {
      setIsPoster(true);
    }
  }, [isSuccess, current_user, data]);

  // Subscribe to history changes to update isAssistant based on URL search params
  useEffect(() => {
    const updateAssistantState = (isInitial = false) => {
      if (
        isPoster &&
        data?.accepted_offers?.filter((elem) => elem.state !== "canceled")
          .length > 0
      ) {
        const searchParams = new URLSearchParams(history.location.search);
        const viewParam = searchParams.get("view");
        setIsAssistant(viewParam !== "job");
        if (isInitial) {
          setIsAssistantInitial(true);
        }
      }
    };
    updateAssistantState(true);
    const unlisten = history.listen(() => {
      updateAssistantState();
      window.scrollTo(0, 0);
    });
    return () => {
      unlisten();
    };
  }, [history, isPoster, data]);

  const switchView = () => {
    if (isAssistant) {
      history.push("?view=job");
    } else {
      history.push("?view=assistant");
    }
  };

  const Wrapper = breakpoints.get({ xs: Block, md: Container.Large });

  const SORT_LIST = [
    {
      title: polyglot.t("job.filter.recommended.name"),
      subtitle: polyglot.t("job.filter.recommended.subtitle"),
      value: "recommended",
    },
    {
      title: polyglot.t("job.filter.pricing.name"),
      value: "pricing",
    },
    {
      title: polyglot.t("job.filter.rating.name"),
      value: "rating",
    },
    {
      title: polyglot.t("job.filter.recency.name"),
      value: "recency",
    },
  ];

  const pendingPrepaids = useMemo(
    () =>
      data?.accepted_offers &&
      getSelectedOrPaidOffers(data?.accepted_offers).filter(
        (offer) => offer.payment_type === DEFERRED_PREPAYMENT && !offer.prepaid
      ),
    [data?.accepted_offers]
  );

  return (
    <JobContext.Provider
      value={{
        isPoster:
          isSuccess && current_user && current_user.id === data.poster?.id,
        data,
        sortList: SORT_LIST,
        public_pages,
        pendingOffers,
        isAssistant,
        switchView,
        isAssistantInitial,
        pendingPrepaids,
        pendingPrepaidIds: pendingPrepaids?.map((offer) => offer.id),
      }}
    >
      {!isSuccess && !data ? (
        <Wrapper>
          <JobSkeleton />
        </Wrapper>
      ) : (
        <>
          <Wrapper>{children}</Wrapper>
          {showSuccessModal && (
            <>
              {isRecurring ? (
                <JobRecurringSuccessModal
                  onClose={() => setShowSuccessModal(false)}
                  isOpen={showSuccessModal}
                  jobber={data?.accepted_offers[0]?.jobber}
                />
              ) : (
                <JobSuccessModal
                  onClose={() => setShowSuccessModal(false)}
                  isOpen={showSuccessModal}
                  avg_time_to_first_offer={data.avg_time_to_first_offer}
                  jobbers={jobbers}
                  location={data.location}
                  private_jobber={data.private_jobber}
                />
              )}
            </>
          )}
        </>
      )}
    </JobContext.Provider>
  );
};
export default JobProvider;
